/* ============================================================
   sourceMap — selection ↔ source-line ↔ DOM-range helpers

   Ported VERBATIM from prototype/reader.html section 6
   (charOffsetWithin / makeRangeFromOffsets / approxLineWithinBlock
   / findEnclosingBlock / findPrecedingHeading) plus the <mark>
   wrap/unwrap. These give stable comment anchors across re-renders:
   a comment stores char offsets into .prose plus a proportional
   source-line span, and we rebuild the highlight from those.
   ============================================================ */

export function findEnclosingBlock(node: Node, prose: HTMLElement): HTMLElement | null {
  let n: Node | null = node
  while (n && n !== prose) {
    if (n.nodeType === 1 && (n as HTMLElement).classList?.contains('md-block')) {
      return n as HTMLElement
    }
    n = n.parentNode
  }
  return null
}

export function findPrecedingHeading(blockEl: HTMLElement): string {
  const ownH = blockEl.querySelector('h1,h2,h3,h4,h5,h6')
  if (ownH) return ownH.textContent?.trim() ?? ''
  let cur = blockEl.previousElementSibling
  while (cur) {
    if (cur.classList && cur.classList.contains('md-block')) {
      const h = cur.querySelector('h1,h2,h3,h4,h5,h6')
      if (h) return h.textContent?.trim() ?? ''
    }
    cur = cur.previousElementSibling
  }
  return ''
}

/** Proportional line within block. */
export function approxLineWithinBlock(
  blockEl: HTMLElement,
  node: Node,
  offset: number,
  side: 'start' | 'end',
): number {
  const blockStart = +(blockEl.dataset.sourceLineStart ?? '0')
  const blockEnd = +(blockEl.dataset.sourceLineEnd ?? '0')
  const blockLen = Math.max(1, (blockEl.textContent ?? '').length)
  const within = charOffsetWithin(blockEl, node, offset)
  const span = Math.max(0, blockEnd - blockStart)
  const ratio = Math.min(1, Math.max(0, within / blockLen))
  const fn = side === 'end' ? Math.ceil : Math.floor
  return blockStart + fn(ratio * span)
}

export function charOffsetWithin(root: Node, node: Node, offset: number): number {
  const r = document.createRange()
  r.setStart(root, 0)
  r.setEnd(node, offset)
  const s = r.toString()
  return s.length
}

export function makeRangeFromOffsets(root: HTMLElement, start: number, end: number): Range | null {
  const range = document.createRange()
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let acc = 0
  let startSet = false
  let cur: Node | null
  while ((cur = walker.nextNode())) {
    const len = cur.nodeValue?.length ?? 0
    if (!startSet && acc + len >= start) {
      range.setStart(cur, start - acc)
      startSet = true
    }
    if (startSet && acc + len >= end) {
      range.setEnd(cur, end - acc)
      return range
    }
    acc += len
  }
  return null
}

/**
 * Re-anchor a comment by its quoted passage rather than stored DOM offsets.
 *
 * Carried-forward / agent comments arrive from a *previous* revision: their
 * `domRangeStart/End` index into the old plan's prose and mean nothing in the
 * revised text. So we search the live prose for the quoted text and rebuild the
 * range where it now sits. `needle` is whitespace-collapsed (as `quotedText`
 * is); the prose carries raw whitespace, so we match against a normalized view
 * and map the hit back to raw character offsets for {@link makeRangeFromOffsets}.
 *
 * Returns null when the passage no longer appears (the agent rewrote or removed
 * it) — the caller treats that as "resolved". First match wins on duplicates.
 */
export function findTextRange(prose: HTMLElement, needle: string): Range | null {
  const target = needle.replace(/\s+/g, ' ').trim()
  if (!target) return null

  // Concatenate all prose text, then build a normalized (single-spaced) copy
  // alongside a map from each normalized char back to its raw index.
  const walker = document.createTreeWalker(prose, NodeFilter.SHOW_TEXT)
  let raw = ''
  let cur: Node | null
  while ((cur = walker.nextNode())) raw += cur.nodeValue ?? ''

  const map: number[] = []
  let normalized = ''
  let prevSpace = false
  for (let i = 0; i < raw.length; i++) {
    const isSpace = /\s/.test(raw[i])
    if (isSpace) {
      if (prevSpace) continue
      normalized += ' '
      map.push(i)
      prevSpace = true
    } else {
      normalized += raw[i]
      map.push(i)
      prevSpace = false
    }
  }

  const idx = normalized.indexOf(target)
  if (idx === -1) return null
  const rawStart = map[idx]
  const rawEnd = map[idx + target.length - 1] + 1
  return makeRangeFromOffsets(prose, rawStart, rawEnd)
}

/**
 * Find a fallback anchor element for a card whose highlight couldn't be placed
 * (a carried comment whose quoted passage is gone). Returns the heading whose
 * text matches `blockHint`, else the first prose block, so the (now resolved)
 * card still has a sensible vertical home in the margin column.
 */
export function findHeadingAnchor(prose: HTMLElement, blockHint: string): HTMLElement | null {
  const hint = (blockHint || '').trim()
  if (hint) {
    const headings = prose.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6')
    for (const h of headings) {
      if ((h.textContent ?? '').trim() === hint) return h
    }
  }
  return (prose.firstElementChild as HTMLElement) ?? null
}

function firstTextNode(root: Node): Text | null {
  return document.createTreeWalker(root, NodeFilter.SHOW_TEXT).nextNode() as Text | null
}

function lastTextNode(root: Node): Text | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let last: Node | null = null
  let cur: Node | null
  while ((cur = walker.nextNode())) last = cur
  return last as Text | null
}

/**
 * Clamp a selection Range so both endpoints lie within `prose`.
 *
 * Dragging a highlight past the last line (into the prose's tall bottom
 * padding, the comment-margin column, or the surrounding grid) leaves an
 * endpoint outside `.prose`; the range's commonAncestor is then outside too,
 * which used to make the draft check fail silently — selecting the last
 * paragraph "didn't always trigger" a comment. Instead of dropping it, we pull
 * any out-of-bounds endpoint back to the nearest text node inside `prose`.
 *
 * Returns null when the selection doesn't actually overlap the prose content
 * (e.g. a stray drag entirely in the margin), so callers can bail cleanly.
 */
export function clampRangeToProse(range: Range, prose: HTMLElement): Range | null {
  if (!range.intersectsNode(prose)) return null
  const r = range.cloneRange()
  if (!prose.contains(r.startContainer)) {
    const first = firstTextNode(prose)
    if (!first) return null
    r.setStart(first, 0)
  }
  if (!prose.contains(r.endContainer)) {
    const last = lastTextNode(prose)
    if (!last) return null
    r.setEnd(last, last.nodeValue?.length ?? 0)
  }
  return r.collapsed ? null : r
}

interface MarkHandlers {
  onClick: (commentId: string) => void
  onEnter: (commentId: string) => void
  onLeave: (commentId: string) => void
}

/**
 * Wrap a DOM Range with <mark> elements carrying the comment id. Splits text
 * nodes at the range boundaries; skips table-structural nodes that can't host
 * a <mark>. Ported verbatim from the prototype's wrapRangeWithMark.
 */
export function wrapRangeWithMark(
  range: Range,
  commentId: string,
  handlers: MarkHandlers,
  origin?: string,
): void {
  const startNode = range.startContainer
  const endNode = range.endContainer
  const startOff = range.startOffset
  const endOff = range.endOffset

  const root =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? (range.commonAncestorContainer.parentNode as Node)
      : range.commonAncestorContainer

  const NON_WRAPPABLE_PARENTS = /^(TR|TBODY|THEAD|TFOOT|TABLE|COLGROUP)$/
  const isWrappable = (n: Node) => !NON_WRAPPABLE_PARENTS.test(n.parentNode?.nodeName || '')

  const textNodes: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n: Node) => {
      if (!range.intersectsNode(n)) return NodeFilter.FILTER_REJECT
      if (!isWrappable(n)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  let cur: Node | null
  while ((cur = walker.nextNode())) textNodes.push(cur as Text)

  if (!textNodes.includes(startNode as Text) && startNode.nodeType === Node.TEXT_NODE) {
    textNodes.unshift(startNode as Text)
  }
  if (!textNodes.includes(endNode as Text) && endNode.nodeType === Node.TEXT_NODE) {
    textNodes.push(endNode as Text)
  }

  for (const tn of textNodes) {
    const len = tn.nodeValue?.length ?? 0
    let from = 0
    let to = len
    if (tn === startNode) from = startOff
    if (tn === endNode) to = endOff
    if (from >= to) continue

    let target: Text = tn
    if (from > 0) target = target.splitText(from)
    if (to - from < (target.nodeValue?.length ?? 0)) target.splitText(to - from)

    const mark = document.createElement('mark')
    mark.dataset.commentId = commentId
    if (origin) mark.dataset.origin = origin
    mark.addEventListener('click', (e) => {
      e.stopPropagation()
      handlers.onClick(commentId)
    })
    mark.addEventListener('mouseenter', () => handlers.onEnter(commentId))
    mark.addEventListener('mouseleave', () => handlers.onLeave(commentId))
    target.parentNode?.insertBefore(mark, target)
    mark.appendChild(target)
  }
}

/** Remove all <mark> elements for a comment, restoring + normalizing text. */
export function unwrapMarks(root: HTMLElement, commentId: string): void {
  root.querySelectorAll(`mark[data-comment-id="${commentId}"]`).forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
    parent.removeChild(mark)
    parent.normalize()
  })
}
